import type { FindingCheckRow } from '../../lib/api.ts'
import { CheckCell, CHECK_CELL_LABEL, cellCount, checkRowName } from '../../lib/findingsList.ts'
import { formatCount, formatRate } from '../../lib/findingsReport.ts'
import { RESPONSE_COLOR, RESPONSE_HELP } from '../../lib/findingsResponsePath.ts'
import { FOCUS_RING } from '../focusRing.ts'
import { FindingsHelp } from './FindingsHelp.tsx'

const COLUMNS = [
  { cell: CheckCell.Total, label: 'Apontamentos', color: RESPONSE_COLOR.total },
  { cell: CheckCell.Responded, label: 'Respondidos', color: RESPONSE_COLOR.responded },
  { cell: CheckCell.Agreed, label: 'Concordaram', color: RESPONSE_COLOR.agreed },
  { cell: CheckCell.Disputed, label: 'Contestaram', color: RESPONSE_COLOR.disputed },
  { cell: CheckCell.Confirmed, label: 'Acertos confirmados', color: RESPONSE_COLOR.confirmed },
  { cell: CheckCell.Error, label: 'Erros confirmados', color: RESPONSE_COLOR.errors },
  { cell: CheckCell.Unanswered, label: 'Sem resposta', color: RESPONSE_COLOR.unreviewedAgreed },
]

/** Selection narrows the checks, while each nonzero cell opens exactly the counted findings. */
export function FindingsByCheck({
  rows,
  onOpenCell,
  selectedCell,
  onClear,
}: {
  rows: readonly FindingCheckRow[]
  onOpenCell: (row: FindingCheckRow, cell: CheckCell) => void
  selectedCell?: CheckCell | undefined
  onClear?: () => void
}) {
  const visible = rows
    .filter(
      (row) =>
        row.responses.total > 0 &&
        (selectedCell === undefined || cellCount(row, selectedCell, 'answered') > 0),
    )
    .sort((a, b) =>
      selectedCell === undefined
        ? b.responses.total - a.responses.total
        : cellCount(b, selectedCell, 'answered') - cellCount(a, selectedCell, 'answered'),
    )
  const columns =
    selectedCell !== undefined && !COLUMNS.some((column) => column.cell === selectedCell)
      ? [
          ...COLUMNS,
          {
            cell: selectedCell,
            label: CHECK_CELL_LABEL[selectedCell],
            color: RESPONSE_COLOR.pendingAudit,
          },
        ]
      : COLUMNS
  const maximum = Math.max(...visible.map((row) => row.responses.total), 1)
  const total = visible.reduce((sum, row) => sum + row.responses.total, 0)
  return (
    <section
      className="bg-background-raised border-stroke-weak min-w-0 rounded-2xl border shadow-sm"
      aria-label="Detalhes por verificação"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div>
          <h2 className="text-tPSemi text-text-strong">Detalhes por verificação</h2>
          <p className="text-tSmall text-text-weak mt-1">
            Abra uma contagem para ver os apontamentos.
          </p>
        </div>
        <span className="text-tSmall text-text-weak tabular-nums">
          {formatCount(visible.length)} tipos · {formatCount(total)} apontamentos
        </span>
      </div>
      {selectedCell !== undefined && (
        <div className="bg-fill-brand-weak border-stroke-brand-weak flex flex-wrap items-center justify-between gap-3 border-y px-5 py-3">
          <span className="text-text-brand text-tSmall inline-flex items-center gap-2">
            Explorando:
            <FindingsHelp
              label={CHECK_CELL_LABEL[selectedCell]}
              content={RESPONSE_HELP[selectedCell]!}
            >
              <strong>{CHECK_CELL_LABEL[selectedCell]}</strong>
            </FindingsHelp>
          </span>
          <button
            type="button"
            onClick={onClear}
            className={`text-text-brand text-tSmall cursor-pointer rounded-md px-2 py-1 underline underline-offset-4 ${FOCUS_RING}`}
          >
            Ver todos os apontamentos
          </button>
        </div>
      )}
      {visible.length === 0 ? (
        <p className="text-tSmall text-text-weak px-5 py-6">Nenhum apontamento neste recorte.</p>
      ) : (
        <div className="w-full overflow-x-auto rounded-b-2xl">
          <table className="w-full min-w-[1080px] table-fixed border-collapse">
            <caption className="sr-only">
              Divergências por tipo de verificação, respostas e resultados da triagem humana.
            </caption>
            <colgroup>
              <col className="w-[240px]" />
              {columns.map((column) => (
                <col key={column.cell} />
              ))}
            </colgroup>
            <thead className="bg-fill-weak border-stroke-weak border-y">
              <tr>
                <th
                  scope="col"
                  className="text-tSmall text-text-weak px-5 py-3 text-left font-medium"
                >
                  Verificação
                </th>
                {columns.map(({ cell, label }) => (
                  <th
                    key={cell}
                    scope="col"
                    className="text-tSmall text-text-weak px-2 py-3 text-center font-medium"
                  >
                    <FindingsHelp label={label} content={RESPONSE_HELP[cell]!} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, index) => (
                <tr
                  key={`${row.check ?? ''}/${row.rule ?? ''}`}
                  className="border-stroke-weak border-b last:border-b-0 hover:bg-fill-weaker"
                >
                  <th scope="row" className="px-5 py-4 text-left align-middle">
                    <div className="flex items-center gap-3">
                      <span className="bg-fill-weak text-text-weak flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-medium tabular-nums">
                        {index + 1}
                      </span>
                      <div>
                        <span className="text-tSmall text-text-strong font-semibold">
                          {checkRowName(row)}
                        </span>
                        <span className="text-text-weak mt-1 block text-[11px] font-normal">
                          {row.rule === null
                            ? `${formatRate(row.responses.total / total)} deste recorte`
                            : 'Regra operacional'}
                        </span>
                      </div>
                    </div>
                  </th>
                  {columns.map(({ cell, color }) => {
                    const value = cellCount(row, cell, 'answered')
                    const active = cell === selectedCell
                    return (
                      <td
                        key={cell}
                        className={`px-2 py-4 text-center align-middle tabular-nums ${active ? 'bg-fill-brand-weak' : ''}`}
                      >
                        {value === 0 ? (
                          <span className="text-tSmall text-text-disabled inline-flex h-8 min-w-10 items-center justify-center">
                            0
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onOpenCell(row, cell)}
                            className={`text-tSmall text-text-strong inline-flex h-8 min-w-10 cursor-pointer items-center justify-center rounded-lg px-2 font-semibold hover:brightness-95 ${FOCUS_RING}`}
                            style={{ background: `color-mix(in srgb, ${color} 12%, transparent)` }}
                            aria-label={`${checkRowName(row)}, ${CHECK_CELL_LABEL[cell]}: ${formatCount(value)}. Ver a lista`}
                          >
                            {formatCount(value)}
                          </button>
                        )}
                        {cell === CheckCell.Total && (
                          <div
                            className="bg-fill-weak mx-auto mt-2 h-1 w-14 overflow-hidden rounded-full"
                            aria-hidden="true"
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(row.responses.total / maximum) * 100}%`,
                                background: color,
                              }}
                            />
                          </div>
                        )}
                        {cell === CheckCell.Responded && (
                          <span className="text-text-weak mt-1 block text-[11px]">
                            {formatRate(row.responses.responded / row.responses.total)}
                          </span>
                        )}
                        {cell === CheckCell.Disputed && (
                          <span className="text-text-weak mt-1 flex flex-wrap justify-center gap-1 text-[11px]">
                            {(
                              [
                                {
                                  cell: CheckCell.PendingConclusion,
                                  value: row.responses.pendingConclusion,
                                  label: 'não enviados',
                                },
                                {
                                  cell: CheckCell.AwaitingTriage,
                                  value: row.responses.awaitingTriage,
                                  label: 'a triar',
                                },
                              ] as const
                            ).map(
                              ({ cell: pendingCell, value: pending, label }) =>
                                pending > 0 && (
                                  <button
                                    key={pendingCell}
                                    type="button"
                                    onClick={() => onOpenCell(row, pendingCell)}
                                    className={`bg-fill-weak cursor-pointer rounded-full px-2 py-0.5 ${FOCUS_RING}`}
                                    aria-label={`${checkRowName(row)}, ${CHECK_CELL_LABEL[pendingCell]}: ${formatCount(pending)}. Ver a lista`}
                                  >
                                    {formatCount(pending)} {label}
                                  </button>
                                ),
                            )}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
